import type { Vec2 } from './types';

export interface ViewportState {
  cssWidth: number;
  cssHeight: number;
  dpr: number;
  scale: number;
  offset: Vec2;
  worldWidth: number;
  worldHeight: number;
}

export class ViewportService {
  state: ViewportState;
  private observer: ResizeObserver;
  private worldWidth = 18;
  private worldHeight = 10;

  constructor(private readonly canvas: HTMLCanvasElement, private readonly onResize: () => void) {
    this.state = { cssWidth: 1, cssHeight: 1, dpr: 1, scale: 1, offset: { x: 0, y: 0 }, worldWidth: 18, worldHeight: 10 };
    this.observer = new ResizeObserver(() => this.resize());
    this.observer.observe(canvas.parentElement ?? canvas);
    this.resize();
  }

  setWorld(width: number, height: number): void {
    this.worldWidth = width;
    this.worldHeight = height;
    this.resize();
  }

  resize(): void {
    const parent = this.canvas.parentElement ?? this.canvas;
    const rect = parent.getBoundingClientRect();
    const cssWidth = Math.max(1, Math.round(rect.width));
    const cssHeight = Math.max(1, Math.round(rect.height));
    const narrow = cssWidth < 720;
    const dpr = Math.min(window.devicePixelRatio || 1, narrow ? 1.6 : 2);
    this.canvas.width = Math.round(cssWidth * dpr);
    this.canvas.height = Math.round(cssHeight * dpr);
    this.canvas.style.width = `${cssWidth}px`;
    this.canvas.style.height = `${cssHeight}px`;
    const gutter = narrow ? 16 : 36;
    const scale = Math.min((cssWidth - gutter * 2) / this.worldWidth, (cssHeight - gutter * 2) / this.worldHeight);
    this.state = {
      cssWidth,
      cssHeight,
      dpr,
      scale,
      offset: {
        x: (cssWidth - this.worldWidth * scale) / 2,
        y: (cssHeight - this.worldHeight * scale) / 2,
      },
      worldWidth: this.worldWidth,
      worldHeight: this.worldHeight,
    };
    this.onResize();
  }

  screenToWorld(point: Vec2): Vec2 {
    return {
      x: (point.x - this.state.offset.x) / this.state.scale,
      y: (point.y - this.state.offset.y) / this.state.scale,
    };
  }

  dispose(): void {
    this.observer.disconnect();
  }
}
